import { useState, type FormEvent } from 'react';
import { ExternalLink, FileText, GitCommitHorizontal, GitPullRequest, Link2Off } from 'lucide-react';
import type { WorkItemView } from '@vwt/contracts';
import { Badge, Button, Field, Input, toast } from '@vwt/ui';
import { isApiError, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { QueryState, SectionTitle, TimeAgo } from '../../components/common.js';

/** Opens provider pages in a new tab without leaking the tracker URL. */
function External({ href, children, className }: { href: string | null; children: React.ReactNode; className?: string }) {
  if (!href) return <span className={className}>{children}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 hover:underline ${className ?? ''}`}>
      {children}
      <ExternalLink className="size-3 shrink-0 text-fg-subtle" aria-hidden />
    </a>
  );
}

/**
 * Pull requests, commits and reported evidence of a work item, each opening its source. Commits
 * that mention the key (VAM-n) link themselves; anything else can be linked by pasting its URL.
 */
export function CodePanel({ item }: { item: WorkItemView }) {
  const { t } = useTranslation();
  const canEdit = item.availableActions.includes('edit');
  const prs = useApiQuery('listPullRequests', { query: { workItemId: item.id, limit: '50' } }, { retry: false });
  const commits = useApiQuery('listWorkCommits', { params: { id: item.id } }, { retry: false });
  const evidence = useApiQuery('listEvidence', { query: { parentKind: 'WORK_ITEM', parentId: item.id } }, { retry: false });
  const link = useApiMutation('linkWorkCode', { invalidate: ['listPullRequests', 'listWorkCommits'] });
  const unlinkCommit = useApiMutation('unlinkWorkCommit', { invalidate: ['listWorkCommits'] });
  const unlinkPr = useApiMutation('unlinkWorkPullRequest', { invalidate: ['listPullRequests'] });
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!url.trim()) return;
    setError(null);
    try {
      const result = await link.mutateAsync({ params: { id: item.id }, body: { url: url.trim() } });
      setUrl('');
      toast.success(t(result?.data.kind === 'PULL_REQUEST' ? 'work.detail.codeTab.linkedPr' : 'work.detail.codeTab.linkedCommit'));
    } catch (e) {
      const code = isApiError(e) ? (e.envelope?.error.details?.fields?.[0]?.code ?? (e.code === 'NOT_FOUND' ? 'not_found' : null)) : null;
      if (code && ['not_connected', 'not_synced', 'not_found', 'unsupported', 'invalid_format'].includes(code)) setError(t(`work.detail.codeTab.errors.${code}`));
      else if (isApiError(e) && e.code === 'VALIDATION_FAILED') setError(t('work.detail.codeTab.errors.unsupported'));
      else toastError(e);
    }
  };

  const evidenceItems = (evidence.data?.data ?? []).filter((e) => e.url);
  return (
    <div className="flex flex-col gap-6">
      <section>
        <SectionTitle>{t('work.detail.codeTab.pullRequests')}</SectionTitle>
        <QueryState query={prs} empty={prs.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">{t('work.detail.codeTab.noPullRequests')}</p> : undefined}>
          <ul className="flex flex-col divide-y divide-border">
            {(prs.data?.data ?? []).map((pr) => {
              const basis = pr.linkedWorkItems.find((w) => w.id === item.id)?.linkBasis;
              return (
                <li key={pr.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <GitPullRequest className="size-4 text-fg-subtle" aria-hidden />
                  <External href={pr.url} className="font-mono text-xs text-fg-muted">
                    {pr.repository.fullName}#{pr.number}
                  </External>
                  <span className="min-w-0 flex-1 truncate">{pr.title}</span>
                  <Badge tone={pr.state === 'MERGED' ? 'accent' : pr.state === 'OPEN' ? 'info' : 'neutral'}>{t(`work.detail.codeTab.prState.${pr.state}`)}</Badge>
                  {basis ? <Badge tone="outline">{t(basis === 'EXPLICIT' ? 'work.detail.codeTab.manual' : basis === 'VAMMO_APPS_CHANGE' ? 'work.detail.codeTab.viaVammoApps' : 'work.detail.codeTab.viaKey')}</Badge> : null}
                  {canEdit ? (
                    <Button size="sm" variant="ghost" aria-label={t('work.detail.codeTab.unlink')} title={t('work.detail.codeTab.unlink')} onClick={() => void unlinkPr.mutateAsync({ params: { id: item.id, childId: pr.id } }).then(() => toast.success(t('work.detail.codeTab.unlinked')), toastError)}>
                      <Link2Off className="size-4" />
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </QueryState>
      </section>

      <section>
        <SectionTitle>{t('work.detail.codeTab.commits')}</SectionTitle>
        <QueryState query={commits} empty={commits.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">{t('work.detail.codeTab.noCommits')}</p> : undefined}>
          <ul className="flex flex-col divide-y divide-border">
            {(commits.data?.data ?? []).map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <GitCommitHorizontal className="size-4 text-fg-subtle" aria-hidden />
                <External href={c.url} className="font-mono text-xs text-accent">
                  {c.shortSha}
                </External>
                <span className="min-w-0 flex-1 truncate">{c.messageSummary}</span>
                <span className="text-xs text-fg-subtle">
                  {c.repository.fullName}
                  {c.authorLogin ? ` · @${c.authorLogin}` : ''}
                  {c.committedAt ? (
                    <>
                      {' · '}
                      <TimeAgo iso={c.committedAt} />
                    </>
                  ) : null}
                </span>
                <Badge tone="outline">{t(c.linkBasis === 'MANUAL' ? 'work.detail.codeTab.manual' : c.linkBasis === 'VAMMO_APPS_CHANGE' ? 'work.detail.codeTab.viaVammoApps' : 'work.detail.codeTab.viaKey')}</Badge>
                {canEdit ? (
                  <Button size="sm" variant="ghost" aria-label={t('work.detail.codeTab.unlink')} title={t('work.detail.codeTab.unlink')} onClick={() => void unlinkCommit.mutateAsync({ params: { id: item.id, childId: c.id } }).then(() => toast.success(t('work.detail.codeTab.unlinked')), toastError)}>
                    <Link2Off className="size-4" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </QueryState>
      </section>

      {evidenceItems.length ? (
        <section>
          <SectionTitle>{t('work.detail.codeTab.evidence')}</SectionTitle>
          <ul className="flex flex-col divide-y divide-border">
            {evidenceItems.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <FileText className="size-4 text-fg-subtle" aria-hidden />
                <External href={e.url} className="min-w-0 flex-1 truncate">
                  {e.title}
                </External>
                <Badge tone="outline">{e.kind}</Badge>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {canEdit ? (
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-2">
          <Field label={t('work.detail.codeTab.linkLabel')} hint={t('work.detail.codeTab.linkHint', { key: item.key })} error={error ?? undefined} htmlFor="code-link-url">
            <div className="flex gap-2">
              <Input id="code-link-url" value={url} onChange={(e) => (setUrl(e.target.value), setError(null))} placeholder="https://github.com/org/repo/commit/…" autoComplete="off" spellCheck={false} />
              <Button type="submit" variant="secondary" loading={link.isPending} disabled={!url.trim()}>
                {t('work.detail.codeTab.link')}
              </Button>
            </div>
          </Field>
        </form>
      ) : null}
    </div>
  );
}
